import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Space,
  Popconfirm,
  Table,
  message,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Tag,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MedicineBoxOutlined,
  SolutionOutlined,
  AuditOutlined,
  MoreOutlined,
  SearchOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { convertTo12HourFormat, LLFormat, TableLoader } from "../../shared";
import { ActionButton } from "../../components";
import {
  ConsentForm,
  MedicalHistory,
  After13DaysInstructions,
  After14DayPrescribeMedicines,
  PostOperationInstructions,
  PreOperationInstructions,
  PrescribeMedicines,
} from "../../components/Printer";
import { DropDownButton } from "../../components/utils";
import { debounce } from "lodash";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

// Session status display
const sessionStatusDisplay = {
  pending: <Tag color="orange">Pending</Tag>,
  confirmed: <Tag color="blue">Confirmed</Tag>,
  completed: <Tag color="green">Completed</Tag>,
  cancel: <Tag color="red">Cancelled</Tag>,
  draft: <Tag color="default">Draft</Tag>,
};

const sessionStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancel" },
  { label: "Draft", value: "draft" },
];

const SessionsList = () => {
  const [filters, setFilters] = useState({
    name: "",
    sessionDate: null, // [from, to]
    service_shortName: "",
    session_status: "",
  });

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Modal states (reused from appointments)
  const [visible, setVisible] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [appointmentID, setAppointmentID] = useState(null);
  const [day, setDay] = useState(null);
  const [prpSessions, setPrpSessions] = useState([]);
  const [fueAppointmentDate, setFueAppointmentDate] = useState(null);

  const fetchAppointments = async (page = 1) => {
    setLoading(true);

    const tokenString = localStorage.getItem("token");
    if (!tokenString) {
      message.error("Authentication token not found.");
      setLoading(false);
      return;
    }

    let token = tokenString;
    try {
      token = JSON.parse(tokenString);
    } catch {}

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("PageNo", pagination.current);
    myHeaders.append("recordsPerPage", pagination.pageSize);

    const raw = JSON.stringify({
      fromDate: filters.sessionDate?.[0] || "",
      toDate: filters.sessionDate?.[1] || "",
      name: filters.name.trim(),
      treament_ids: filters.service_shortName || "",
      app_status: "",
      pageNo: page,
      recordsPerPage: pagination.pageSize,
    });

    try {
      const response = await fetch(
        "https://api-qa.hairplusinternational.com/api/appointment/viewAll",
        { method: "POST", headers: myHeaders, body: raw },
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (!result.success) throw new Error(result.message || "Failed");

      const appointments = result.data || [];
      const total = result.totalRecords || 0;

      // Flatten sessions
      const sessions = [];
      appointments.forEach((appt) => {
        const client = appt.client;
        const appointmentStatus = appt.status;

        appt.sessions?.forEach((session) => {
          const service = session.service;
          const sessionDate = session.sessionDateTime.split(" ")[0];
          const sessionTime = session.sessionDateTime.split(" ")[1];

          sessions.push({
            key: session.id,
            sessionId: session.id,
            appointmentId: appt.id,
            client,
            serviceName: service.shortName,
            sessionDate,
            sessionTime,
            sessionStatus: session.status,
            note: session.note,
            price: session.price,
            appointmentStatus,
          });
        });
      });

      // Sort by session date and time
      sessions.sort((a, b) =>
        `${a.sessionDate} ${a.sessionTime}`.localeCompare(
          `${b.sessionDate} ${b.sessionTime}`,
        ),
      );

      setDataSource(sessions);
      setPagination((prev) => ({ ...prev, current: page, total }));
    } catch (error) {
      console.error(error);
      message.error("Failed to load sessions");
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      fetchAppointments(1);
    }, 600),
    [filters],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchAppointments(1);
  }, [
    filters.sessionDate,
    filters.service_shortName,
    filters.session_status,
    pagination.pageSize,
  ]);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [filters.name, debouncedFetch]);

  useEffect(() => {
    fetchAppointments(1);
  }, []);

  const handleNameChange = (e) =>
    setFilters((prev) => ({ ...prev, name: e.target.value }));
  const handleDateChange = (_dates, dateStrings) => {
    setFilters((prev) => ({
      ...prev,
      sessionDate: dateStrings[0] && dateStrings[1] ? dateStrings : null,
    }));
  };
  const handleServiceChange = (value) =>
    setFilters((prev) => ({ ...prev, service_shortName: value || "" }));
  const handleStatusChange = (value) =>
    setFilters((prev) => ({ ...prev, session_status: value || "" }));
  const handleReset = () => {
    setFilters({
      name: "",
      sessionDate: null,
      service_shortName: "",
      session_status: "",
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    fetchAppointments(newPagination.current);
  };

  const handleSessionStatusChange = async (sessionId, newStatus) => {
    const tokenString = localStorage.getItem("token");
    let token = tokenString;
    try {
      token = JSON.parse(tokenString);
    } catch {}

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);
    myHeaders.append("Content-Type", "application/json");

    try {
      const response = await fetch(
        "https://api-qa.hairplusinternational.com/api/appointment/changeSessionStatus",
        {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify({ id: sessionId, status: newStatus }),
        },
      );

      const result = await response.json();
      if (result.success) {
        message.success("Session status updated!");
        fetchAppointments(pagination.current);
      } else {
        message.error(result.message || "Failed");
      }
    } catch {
      message.error("Failed to update status");
    }
  };

  const handlePrescriptionMenuClick = (appointmentID, clientInfo, day) => {
    setAppointmentID(appointmentID);
    setClientInfo(clientInfo);
    setDay(day);
    setVisible(day === 14 ? 7 : 3);
  };

  const columns = [
    {
      title: "Patient",
      dataIndex: ["client", "name"],
      key: "patient",
    },
    {
      title: "Phone",
      render: (_, record) => "+92 " + (record.client?.telephone || "-"),
      key: "phone",
    },
    {
      title: "Session Date & Time",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <div>{LLFormat(record.sessionDate)}</div>
          <div style={{ fontSize: 12, color: "#666" }}>
            {convertTo12HourFormat(record.sessionTime)}
          </div>
        </Space>
      ),
      key: "sessionDateTime",
      sorter: (a, b) =>
        `${a.sessionDate} ${a.sessionTime}`.localeCompare(
          `${b.sessionDate} ${b.sessionTime}`,
        ),
    },
    {
      title: "Service",
      dataIndex: "serviceName",
      key: "service",
    },
    {
      title: "Status",
      dataIndex: "sessionStatus",
      key: "status",
      render: (status) => sessionStatusDisplay[status] || "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <ActionButton
            title="Consent Form"
            icon={<AuditOutlined className="text-orangered" />}
            onClick={() => {
              setClientInfo(record.client);
              setVisible(1);
            }}
          />

          <ActionButton
            title="Medical History"
            icon={<SolutionOutlined className="text-blue" />}
            onClick={() => {
              setAppointmentID(record.appointmentId);
              setVisible(2);
            }}
          />

          {record.appointmentStatus === "confirmed" && (
            <>
              <ActionButton
                title="Post Operation"
                icon={<MedicineBoxOutlined className="text-grey" />}
                onClick={() => {
                  setVisible(4);
                  setPrpSessions([]);
                  setFueAppointmentDate(record.sessionDate);
                }}
              />
              <DropDownButton
                icon={<MedicineBoxOutlined className="text-purple" />}
                menu={{
                  items: [
                    {
                      label: "1st Day Prescription",
                      key: "1",
                      onClick: () =>
                        handlePrescriptionMenuClick(
                          record.appointmentId,
                          record.client,
                          1,
                        ),
                    },
                    {
                      label: "4th Day Prescription",
                      key: "2",
                      onClick: () =>
                        handlePrescriptionMenuClick(
                          record.appointmentId,
                          record.client,
                          4,
                        ),
                    },
                    {
                      label: "14th Day Prescription",
                      key: "3",
                      onClick: () =>
                        handlePrescriptionMenuClick(
                          record.appointmentId,
                          record.client,
                          14,
                        ),
                    },
                    {
                      label: "After 13 Days Instructions",
                      key: "4",
                      onClick: () => setVisible(6),
                    },
                  ],
                }}
              />
            </>
          )}

          <DropDownButton
            icon={<MoreOutlined />}
            menu={{
              items: (() => {
                const status = record.sessionStatus;
                const items = [];
                if (status !== "completed") {
                  items.push({
                    label: "Completed Session",
                    onClick: () =>
                      handleSessionStatusChange(record.sessionId, "completed"),
                  });
                }
                if (status !== "cancel") {
                  items.push({
                    label: "Cancel Session",
                    onClick: () =>
                      handleSessionStatusChange(record.sessionId, "cancel"),
                  });
                }
                return items;
              })(),
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" className="width-100" size="large">
        <Title level={4}>
          <CalendarOutlined /> Sessions
        </Title>

        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} lg={6}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500 }}>Client Name / Phone</label>
            </div>
            <Input
              placeholder="Search patient..."
              prefix={<SearchOutlined />}
              value={filters.name}
              onChange={handleNameChange}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} lg={7}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500 }}>Session Date Range</label>
            </div>
            <RangePicker
              style={{ width: "100%" }}
              value={
                filters.sessionDate
                  ? [
                      dayjs(filters.sessionDate[0]),
                      dayjs(filters.sessionDate[1]),
                    ]
                  : null
              }
              onChange={handleDateChange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>

          <Col xs={24} sm={8} lg={4}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500 }}>Service Type</label>
            </div>
            <Select
              style={{ width: "100%" }}
              placeholder="All Services"
              value={filters.service_shortName || undefined}
              onChange={handleServiceChange}
              allowClear
              options={[
                { value: "FUE", label: "FUE" },
                { value: "PRP", label: "PRP" },
                { value: "Laser", label: "Laser" },
              ]}
            />
          </Col>

          <Col xs={24} sm={8} lg={4}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: 500 }}>Session Status</label>
            </div>
            <Select
              style={{ width: "100%" }}
              placeholder="All"
              value={filters.session_status || undefined}
              onChange={handleStatusChange}
              allowClear
              options={sessionStatusOptions}
            />
          </Col>

          <Col xs={24} sm={8} lg={3}>
            <Button onClick={handleReset} style={{ width: "100%", height: 40 }}>
              Reset
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="sessionId"
          loading={{ ...TableLoader, spinning: loading }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            pageSizeOptions: ["20", "50", "100"],
            showTotal: (total) => (
              <div className="totalCountOfTable">Total Sessions: {total}</div>
            ),
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Space>

      {/* Reused Modals */}
      <ConsentForm
        visible={visible === 1}
        clientInfo={clientInfo}
        onClose={() => {
          setClientInfo(null);
          setVisible(false);
        }}
      />
      <MedicalHistory
        visible={visible === 2}
        appointmentID={appointmentID}
        onClose={() => {
          setVisible(false);
          setAppointmentID(null);
        }}
      />
      <PrescribeMedicines
        visible={visible === 3}
        clientInfo={clientInfo}
        appointmentID={appointmentID}
        day={day}
        onClose={() => {
          setVisible(false);
          setClientInfo(null);
          setAppointmentID(null);
          setDay(null);
        }}
      />
      <After14DayPrescribeMedicines
        visible={visible === 7}
        clientInfo={clientInfo}
        appointmentID={appointmentID}
        day={day}
        onClose={() => {
          setVisible(false);
          setClientInfo(null);
          setAppointmentID(null);
          setDay(null);
        }}
      />
      <PostOperationInstructions
        visible={visible === 4}
        prpSessions={prpSessions}
        fueAppointmentDate={fueAppointmentDate}
        onClose={() => {
          setVisible(false);
          setPrpSessions([]);
          setFueAppointmentDate(null);
        }}
      />
      <PreOperationInstructions
        visible={visible === 5}
        onClose={() => setVisible(false)}
      />
      <After13DaysInstructions
        visible={visible === 6}
        onClose={() => setVisible(false)}
      />
    </>
  );
};

export { SessionsList };
